import Timer from "@stardust/timer";
import Slider from "@stardust/slider";
import { registerThemeEditorSection } from "@stardust/theme-editor";

const AUTOPLAY_INTERVAL_MS = 4000;
const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

class StoreMessagesSection {
  constructor(element) {
    this.element = element;
    this.slider = null;
    this.timers = [];
    this.abort = null;
    this.userPaused = false;
  }

  load() {
    this.abort = new AbortController();
    const { signal } = this.abort;

    // iOS Safari only applies :active when a touch listener is present, and the hover/tap
    // background shift on linked messages relies on it.
    this.element.addEventListener("touchstart", () => {}, { passive: true, signal });

    // Rotate through messages when there is more than one.
    const sliderEl = this.element.querySelector("[data-slider]");
    if (sliderEl) {
      this.userPaused = window.matchMedia(REDUCED_MOTION_QUERY).matches;
      this.slider = new Slider(sliderEl, {
        snap: true,
        autoplay: !this.userPaused,
        interval: AUTOPLAY_INTERVAL_MS
      });
      this.bindPauseControl(signal);
    }

    this.element.querySelectorAll("[data-timer]").forEach(timerElement => {
      this.timers.push(new Timer(timerElement));
    });
  }

  /**
   * Pause/play button plus pause-on-hover and pause-on-focus, so rotation never moves content
   * out from under a reader or a keyboard user (WCAG 2.2.2). Rotation runs only when none of
   * the three reasons to pause holds: the shopper paused it, the pointer is over the bar, or
   * focus is inside it. Each event updates its own flag, then one check decides.
   *
   * @param {AbortSignal} signal
   * @returns {void}
   * @private
   */
  bindPauseControl(signal) {
    const button = this.element.querySelector("[data-store-messages-pause]");
    let hovered = false;
    let focused = false;

    const update = () => {
      button?.setAttribute("aria-pressed", String(this.userPaused));
      this.slider.setAutoplay(!this.userPaused && !hovered && !focused);
    };

    update();

    button?.addEventListener("click", () => {
      this.userPaused = !this.userPaused;
      update();
    }, { signal });

    this.element.addEventListener("mouseenter", () => {
      hovered = true;
      update();
    }, { signal });
    this.element.addEventListener("mouseleave", () => {
      hovered = false;
      update();
    }, { signal });
    this.element.addEventListener("focusin", () => {
      focused = true;
      update();
    }, { signal });
    this.element.addEventListener("focusout", (event) => {
      if (this.element.contains(event.relatedTarget)) return;
      focused = false;
      update();
    }, { signal });
  }

  unload() {
    this.abort?.abort();
    this.abort = null;
    this.slider?.destroy();
    this.slider = null;
    this.timers.forEach(timer => timer.stop());
    this.timers = [];
  }
}

// Auto-initialize sections on DOM ready
if (typeof document !== 'undefined') {
  const initStoreMessages = () => {
    document.querySelectorAll('[data-section-type="store-messages"]').forEach(section => {
      new StoreMessagesSection(section).load();
    });
  };

  initStoreMessages();
}

registerThemeEditorSection('store-messages', StoreMessagesSection);
